import { RequireOnlyOne } from "@cervval.js/common/src/tools/object.tools";
import { DAYS, TsDate, UnixTimestamp } from "@cervval.js/common/src/tools/timestamps.tools";

export type Second = number & { _seconds: boolean }
export type SecondFromMidnight = Second & { _secondAfterMidnight: boolean }

/**
 * @returns the number typed as Second
 */
export function toSecond(secs: number): Second {
    return secs as Second;
}

/**
 * @returns the number typed as SecondFromMidnight
 */
export function toSecondFromMidnight(secs: number): SecondFromMidnight {
    return secs as SecondFromMidnight;
}

/**
 * Calendar
 */
export interface WorkCalendar {
    id?: number;
    default: WorkCalendarDefaultWeek;
    exceptions: SortedWorkCalendarExceptionArray;
}

export type SortedWorkCalendarExceptionArray = WorkCalendarExceptionWeek[] & { _sorted: boolean }

export interface WorkCalendarDefaultWeekContent {
    [DAYS.MONDAY]: WorkCalendarDay;
    [DAYS.TUESDAY]: WorkCalendarDay;
    [DAYS.WEDNESDAY]: WorkCalendarDay;
    [DAYS.THURSDAY]: WorkCalendarDay;
    [DAYS.FRIDAY]: WorkCalendarDay;
    [DAYS.SATURDAY]: WorkCalendarDay;
    [DAYS.SUNDAY]: WorkCalendarDay;
}

/**
 * Week with all days mandatory and no validity dates
 */
export interface WorkCalendarDefaultWeek {
    week: WorkCalendarDefaultWeekContent;
    _duration?: Second;
}

/**
 * Week with optional days and a validity period
 */
export interface WorkCalendarExceptionWeek {
    week: Partial<WorkCalendarDefaultWeekContent>;
    start: UnixTimestamp;
    finish: UnixTimestamp;
    _duration?: Second;
}

/**
 * Timeslot betwen 0 → 24h
 */
export interface TimeSlot {
    start: SecondFromMidnight;
    finish: SecondFromMidnight;
}
export type SortedTimeSlotArray = TimeSlot[] & { _sorted: boolean }

export interface WorkCalendarPartition {
    previous?: WorkCalendarPartition;
    next?: WorkCalendarPartition;
    from?: UnixTimestamp;
    to?: UnixTimestamp;
    calendarItems: (WorkCalendarExceptionWeek | WorkCalendarDefaultWeek)[];
    _duration?: Second;
}

/**
 * Default day of the calendar
 */
export interface WorkCalendarDay {
    timeslots: SortedTimeSlotArray | TimeSlot[];
    _duration?: Second;
}

export interface WeekTimeSlots extends WithID {
    week: {
        [DAYS.MONDAY]?: { timeslots: TimeSlot[] },
        [DAYS.TUESDAY]?: { timeslots: TimeSlot[] },
        [DAYS.WEDNESDAY]?: { timeslots: TimeSlot[] },
        [DAYS.THURSDAY]?: { timeslots: TimeSlot[] },
        [DAYS.FRIDAY]?: { timeslots: TimeSlot[] },
        [DAYS.SATURDAY]?: { timeslots: TimeSlot[] },
        [DAYS.SUNDAY]?: { timeslots: TimeSlot[] }
    };
}

export interface WorkCalendarOptions<Calendar extends WithID, Exception extends WithID, Week extends WeekTimeSlots> {
    calendars: Calendar[];
    calendarWeekIdKey: keyof Calendar;

    exceptions: Exception[];
    exceptionCalendarIdKey: keyof Exception;
    exceptionWeekIdKey: keyof Exception;
    exceptionStartKey: keyof Exception;
    exceptionFinishKey: keyof Exception;

    weeks: Week[];
}

export interface WithID {
    id: number;
}

/**
 * A date or a duration in seconds
 */
export type DateOrDuration = RequireOnlyOne<{ date: TsDate, duration: Second }>;

export enum WorkDateType {
    START = 1,
    FINISH = 2
}

/**
 * The Cursor goes from start to target via several iterations that fill result and update position until targetReached
 */
export interface WorkCalendarCursor {
    currentPartition: WorkCalendarPartition;    // The partition on which is the cursor
    start: TsDate;           // The earliest start of the cursor (on workable UTS)
    position: TsDate;               // The current cursor position in UTS
    target: DateOrDuration;         // The cursor target
    dateType: WorkDateType;       // The target date Type (Start or finish date)
    direction: 1 | -1;              // The cursor direction
    result: {
        targetReached: boolean,     // Is the target reached ?
        weeks: number,              // Worked full weeks (regardless the duration within the week)
        days: number,               // Worked full days (regardless the duration within the day)
        additionalSeconds: number,  // Worked seconds that are not on full days
        elapsed: number             // Total worked duration in seconds. Can be positive (forward scan) or negative (reverse scan)
    };
}

/** Object containing Start, Finish and Duration */
export interface StartDurationFinish {
    start: TsDate;
    duration: Second;
    finish: TsDate;
}

/** Calendar mode : not implemented yet.
 * WRAP : each working timeslots "use" duration up to resolution (duration = 0)
 * SLOT : todo : duration must fit a timeslot to be resolve
 */
export enum CalendarMode {
    WRAP,
    SLOT // ! not implemented
}

/** All the period types in a calendar */
export enum WorkCalendarPeriodType {
    /** Means the instant is in a defined worked timeslot @see {@link TimeSlot} @see {@link WeekTimeSlots} */
    WORKED = 1,
    /** Means the instant is in a gap between two defined timeslots @see {@link TimeSlot} @see {@link WeekTimeSlots} */
    NOT_WORKED
}

/** The period given from an instant */
export interface WorkCalendarPeriod {
    /** @see {@link WorkCalendarPeriodType} */
    type: WorkCalendarPeriodType;
    /** The period start, can be undefined */
    start?: UnixTimestamp;
    /** The period finish, can be undefined */
    finish?: UnixTimestamp;
}